/**
 * Backend AI Trading Service Communication
 */

interface AITradingStatus {
  isRunning: boolean;
  activeStrategies: number;
  lastSignalTimes: Record<string, number>;
}

interface AITradingResponse {
  status: 'success' | 'error';
  message: string;
}

class BackendAIService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  }

  /**
   * Get AI trading service status
   */
  async getStatus(): Promise<AITradingStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/api/ai-trading/status`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching AI trading status:', error);
      return {
        isRunning: false,
        activeStrategies: 0,
        lastSignalTimes: {}
      };
    }
  }

  /**
   * Start AI trading service
   */
  async startService(): Promise<AITradingResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/ai-trading/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error starting AI trading service:', error);
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Stop AI trading service
   */
  async stopService(): Promise<AITradingResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/ai-trading/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      // Check if it's a connection error (server not running)
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        // Server is not running - this is expected in some cases
        return {
          status: 'success',
          message: 'Backend service not running - AI stopped locally'
        };
      }
      
      // Other errors - log but don't spam console
      if (process.env.NODE_ENV === 'development') {
        console.warn('Backend AI service stop failed:', error);
      }
      
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Force reload strategies from database
   */
  async reloadStrategies(): Promise<AITradingResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/ai-trading/reload-strategies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error reloading AI trading strategies:', error);
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if backend service is available
   */
  async isServiceAvailable(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/health`, {
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
      return response.ok;
    } catch (error) {
      // Silently return false if service is not available
      // This prevents console errors when backend service is not running
      return false;
    }
  }

  /**
   * Get AI trading logs for a user
   */
  async getAITradingLogs(userId: string, limit: number = 50): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/ai-trading/logs/${userId}?limit=${limit}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching AI trading logs:', error);
      return [];
    }
  }
}

export const backendAIService = new BackendAIService();
export default backendAIService;
